// v10/src/components/admin/lifespan/RegisterReplacementModal.js
//
// Modal "Registrar reemplazo" del módulo Vida límite (A6.1) — Tarea 2.
// POST /api/aircraft-components/:id/replacements
//
// El reemplazo es una transacción atómica en backend que:
//   1. Inserta una fila en component_replacements
//   2. Resetea installed_at_date / installed_at_horometer del componente
//   3. Opcionalmente actualiza serial_number
//   4. (Vinculación A6.1 ↔ A8) Si el toggle "Descontar del stock" está
//      activo, en la misma tx descuenta del producto seleccionado e
//      inserta un movement de tipo dispatch.
//
// Validación cliente-side:
//   • replacement_date no es futura
//   • replacement_date >= installed_at_date del componente
//   • replacement_horometer >= installed_at_horometer del componente
//   • Si dispatch ON: producto seleccionado, qty > 0, qty <= stock disponible

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, RefreshCw, Package, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import componentLifespanService from '../../../services/componentLifespanService';
import inventoryService from '../../../services/inventoryService';

// Mapeo cliente-side; replica condor360-backend/utils/categoryMapping.js
// para minimizar roundtrips. Si no hay match fuerte, mostramos todos los
// productos compatibles con la aeronave (sin filtro de categoría).
const COMPONENT_TO_INVENTORY_CATEGORY = {
  oil_filter:  'oil_filter',
  spark_plug:  'spark_plug',
  elt_battery: 'battery',
};

const todayIso = () => new Date().toISOString().split('T')[0];

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatHours = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const formatQty = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
};

const formatDateAR = (value) => {
  const d = toDateInput(value);
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const RegisterReplacementModal = ({
  component,
  onSuccess,
  onClose,
  darkMode,
  locale = 'es-AR',
}) => {
  const { t } = useTranslation('admin');

  const installedDate = toDateInput(component?.installed_at_date);
  const installedHoro = component?.installed_at_horometer != null
    ? Number(component.installed_at_horometer)
    : 0;

  const [form, setForm] = useState({
    replacement_date: todayIso(),
    replacement_horometer: '',
    new_serial_number: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Estado del bloque de stock dispatch ──────────────────────────
  const [dispatchEnabled, setDispatchEnabled]     = useState(false);
  const [products, setProducts]                   = useState([]);
  const [productsLoading, setProductsLoading]     = useState(false);
  const [productsError, setProductsError]         = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [quantity, setQuantity]                   = useState('1');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  // ── Fetch de productos compatibles al abrir el modal ─────────────
  useEffect(() => {
    if (!component?.aircraft_id) return;
    const mappedCategory = component?.category
      ? COMPONENT_TO_INVENTORY_CATEGORY[component.category] || null
      : null;

    let cancelled = false;
    setProductsLoading(true);
    setProductsError('');
    inventoryService
      .getCompatibleWithAircraft(component.aircraft_id, mappedCategory)
      .then((res) => {
        if (cancelled || !isMounted.current) return;
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        setProducts(data);
      })
      .catch((err) => {
        if (cancelled || !isMounted.current) return;
        const msg = err?.response?.data?.message || err?.message || 'unknown';
        setProductsError(msg);
        setProducts([]);
      })
      .finally(() => {
        if (cancelled || !isMounted.current) return;
        setProductsLoading(false);
      });
    return () => { cancelled = true; };
  }, [component?.aircraft_id, component?.category]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Producto seleccionado (objeto completo) para preview de stock
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) || null,
    [products, selectedProductId],
  );

  // Productos no-serialized → seleccionables. Los serialized aparecen
  // pero deshabilitados con tooltip.
  const hasAnyDispatchable = useMemo(
    () => products.some((p) => p.tracking_mode !== 'serialized'),
    [products],
  );

  const dispatchToggleDisabled = !hasAnyDispatchable || productsLoading || !!productsError;

  // ── Validación combinada (replacement + dispatch opcional) ───────
  const validation = useMemo(() => {
    const errors = [];

    if (!form.replacement_date) {
      errors.push(t('lifespan_validation_replacement_date_required'));
    } else {
      if (form.replacement_date > todayIso()) {
        errors.push(t('lifespan_validation_replacement_date_future'));
      }
      if (installedDate && form.replacement_date < installedDate) {
        errors.push(t('lifespan_validation_replacement_date_before_install', { date: formatDateAR(installedDate) }));
      }
    }

    if (form.replacement_horometer === '') {
      errors.push(t('lifespan_validation_replacement_horometer_required'));
    } else {
      const h = Number(form.replacement_horometer);
      if (!Number.isFinite(h)) {
        errors.push(t('lifespan_validation_replacement_horometer_required'));
      } else if (h < installedHoro) {
        errors.push(t('lifespan_validation_replacement_horometer_below_install', {
          value: formatHours(installedHoro, locale),
        }));
      }
    }

    if (dispatchEnabled) {
      if (!selectedProductId) {
        errors.push(t('lifespan_replacement_dispatch_select_product'));
      } else if (selectedProduct?.tracking_mode === 'serialized') {
        errors.push(t('lifespan_replacement_dispatch_serialized_disabled'));
      } else {
        const qty = Number(quantity);
        if (!Number.isFinite(qty) || qty <= 0) {
          errors.push(t('lifespan_replacement_dispatch_quantity_invalid'));
        } else if (selectedProduct) {
          const available = Number(selectedProduct.current_stock_quantity);
          if (Number.isFinite(available) && qty > available) {
            errors.push(t('lifespan_replacement_dispatch_quantity_exceeds', {
              available: formatQty(available, locale),
            }));
          }
        }
      }
    }

    return { errors, ok: errors.length === 0 };
  }, [
    form, installedDate, installedHoro, locale, t,
    dispatchEnabled, selectedProductId, selectedProduct, quantity,
  ]);

  // Preview de stock
  const stockPreview = useMemo(() => {
    if (!dispatchEnabled || !selectedProduct) return null;
    const before = Number(selectedProduct.current_stock_quantity);
    const qty    = Number(quantity);
    if (!Number.isFinite(before) || !Number.isFinite(qty)) return null;
    const after  = before - qty;
    const min    = Number(selectedProduct.minimum_stock_quantity);
    const lowAfter = Number.isFinite(min) && min > 0 && after >= 0 && after < min;
    const negative = after < 0;
    return { before, after, min, lowAfter, negative, unit: selectedProduct.stock_unit };
  }, [dispatchEnabled, selectedProduct, quantity]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading || !component?.id) return;

    const payload = {
      replacement_date: form.replacement_date,
      replacement_horometer: Number(form.replacement_horometer),
    };
    if (form.new_serial_number.trim()) payload.new_serial_number = form.new_serial_number.trim();
    if (form.reason.trim()) payload.reason = form.reason.trim();

    if (dispatchEnabled && selectedProductId) {
      payload.stock_dispatch = {
        inventory_item_id: selectedProductId,
        quantity:          Number(quantity),
        reason:            t('lifespan_replacement_dispatch_default_reason', { component: component?.name || '' }),
      };
    }

    setLoading(true); setError('');
    try {
      await componentLifespanService.createReplacement(component.id, payload);
      if (!isMounted.current) return;
      onSuccess?.({
        date: formatDateAR(form.replacement_date),
        horometer: formatHours(payload.replacement_horometer, locale),
      });
      onClose();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.response?.data?.message || t('lifespan_validation_network_error'));
      setLoading(false);
    }
  };

  // Estilos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };

  const productLabel = (p) => {
    const parts = [p.name];
    if (p.manufacturer) parts.push(`· ${p.manufacturer}`);
    const stockStr = `${formatQty(p.current_stock_quantity, locale)} ${p.stock_unit}`;
    parts.push(`(${stockStr})`);
    if (p.is_universal) parts.push(`· ${t('lifespan_replacement_dispatch_universal_label')}`);
    return parts.join(' ');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 560, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#0891b2,#0e7490)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(8,145,178,0.3)',
              }}
            >
              <RefreshCw style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('component_replacement_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted, maxWidth: 380 }}>
                {t('component_replacement_modal_subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Componente afectado */}
        <div
          style={{
            padding: '10px 14px', borderRadius: 12, marginBottom: 16,
            background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
          }}
        >
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fgPrimary }}>
            {component?.name}
          </p>
          {component?.aircraft_registration && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
              {component.aircraft_registration}
              {component.aircraft_model ? ` · ${component.aircraft_model}` : ''}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('component_replacement_modal_label_date')}</p>
              <input
                type="date" max={todayIso()} min={installedDate || undefined}
                style={{ ...inputStyle, cursor: 'text' }}
                value={form.replacement_date}
                onChange={(e) => setField('replacement_date', e.target.value)}
              />
            </div>
            <div>
              <p style={labelStyle}>{t('component_replacement_modal_label_horometer')}</p>
              <input
                type="number" min={installedHoro} step="0.1" style={inputStyle}
                value={form.replacement_horometer}
                onChange={(e) => setField('replacement_horometer', e.target.value)}
                placeholder={t('component_replacement_modal_placeholder_horometer')}
              />
              <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                {t('component_replacement_modal_hint_horometer', {
                  min: formatHours(installedHoro, locale),
                })}
              </p>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_replacement_modal_label_serial')}</p>
            <input
              type="text" maxLength={100} style={inputStyle}
              value={form.new_serial_number}
              onChange={(e) => setField('new_serial_number', e.target.value)}
              placeholder={t('component_replacement_modal_placeholder_serial')}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('component_replacement_modal_label_reason')}</p>
            <textarea
              rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 70, cursor: 'text' }}
              value={form.reason}
              onChange={(e) => setField('reason', e.target.value)}
              placeholder={t('component_replacement_modal_placeholder_reason')}
            />
          </div>

          {/* ── Vinculación con stock (A6.1 ↔ A8) ──────────────────── */}
          <div
            style={{
              borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
              paddingTop: 16, marginBottom: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Package style={{ width: 16, height: 16, color: fgMuted }} />
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fgPrimary }}>
                    {t('lifespan_replacement_dispatch_toggle')}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: fgMuted, maxWidth: 360 }}>
                    {productsLoading
                      ? t('lifespan_replacement_dispatch_loading')
                      : productsError
                        ? t('lifespan_replacement_dispatch_error', { error: productsError })
                        : !hasAnyDispatchable
                          ? t('lifespan_replacement_dispatch_no_compatible')
                          : t('lifespan_replacement_dispatch_toggle_hint')}
                  </p>
                </div>
              </div>
              <label
                title={dispatchToggleDisabled ? t('lifespan_replacement_dispatch_no_compatible') : undefined}
                style={{
                  position: 'relative', display: 'inline-block',
                  width: 42, height: 24, flexShrink: 0,
                  cursor: dispatchToggleDisabled ? 'not-allowed' : 'pointer',
                  opacity: dispatchToggleDisabled ? 0.45 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={dispatchEnabled}
                  disabled={dispatchToggleDisabled}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setDispatchEnabled(on);
                    if (!on) {
                      setSelectedProductId('');
                      setQuantity('1');
                    }
                  }}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    position: 'absolute', inset: 0, borderRadius: 999,
                    background: dispatchEnabled
                      ? 'linear-gradient(135deg,#0891b2,#0e7490)'
                      : (darkMode ? 'rgba(255,255,255,0.18)' : '#cbd5e1'),
                    transition: 'background 0.2s',
                  }}
                />
                <span
                  style={{
                    position: 'absolute', top: 3, left: dispatchEnabled ? 21 : 3,
                    width: 18, height: 18, borderRadius: '50%', background: 'white',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  }}
                />
              </label>
            </div>

            {dispatchEnabled && (
              <div
                style={{
                  marginTop: 14, padding: 14, borderRadius: 12,
                  background: darkMode ? 'rgba(8,145,178,0.08)' : 'rgba(8,145,178,0.05)',
                  border: darkMode ? '1px solid rgba(8,145,178,0.25)' : '1px solid rgba(8,145,178,0.18)',
                }}
              >
                <div style={{ marginBottom: 10 }}>
                  <p style={labelStyle}>{t('lifespan_replacement_dispatch_product')}</p>
                  <select
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                  >
                    <option value="">{t('lifespan_replacement_dispatch_select_product')}</option>
                    {products.map((p) => {
                      const isSerialized = p.tracking_mode === 'serialized';
                      return (
                        <option
                          key={p.id} value={p.id}
                          disabled={isSerialized}
                          title={isSerialized ? t('lifespan_replacement_dispatch_serialized_disabled') : undefined}
                        >
                          {productLabel(p)}{isSerialized ? ` — ${t('lifespan_replacement_dispatch_serialized_disabled')}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <p style={labelStyle}>{t('lifespan_replacement_dispatch_quantity')}</p>
                  <input
                    type="number" min={0} step="0.01" style={inputStyle}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="1"
                  />
                  {selectedProduct && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                      {selectedProduct.stock_unit}
                    </p>
                  )}
                </div>

                {stockPreview && (
                  <div
                    style={{
                      marginTop: 6, padding: '10px 12px', borderRadius: 10,
                      background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
                      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                      fontSize: 12,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: fgMuted }}>
                      <span>{t('lifespan_replacement_dispatch_stock_current')}</span>
                      <span style={{ color: fgPrimary, fontWeight: 700 }}>
                        {formatQty(stockPreview.before, locale)} {stockPreview.unit}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: fgMuted, marginTop: 4 }}>
                      <span>{t('lifespan_replacement_dispatch_stock_after')}</span>
                      <span
                        style={{
                          color: stockPreview.negative ? '#ef4444' : (stockPreview.lowAfter ? '#d97706' : fgPrimary),
                          fontWeight: 700,
                        }}
                      >
                        {formatQty(stockPreview.after, locale)} {stockPreview.unit}
                      </span>
                    </div>
                    {stockPreview.negative && (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                        <AlertTriangle style={{ width: 14, height: 14 }} />
                        {t('lifespan_replacement_dispatch_stock_insufficient')}
                      </p>
                    )}
                    {!stockPreview.negative && stockPreview.lowAfter && (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#d97706', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                        <AlertTriangle style={{ width: 14, height: 14 }} />
                        {t('lifespan_replacement_dispatch_stock_low', {
                          minimum: formatQty(stockPreview.min, locale),
                        })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#0891b2,#0e7490)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <RefreshCw style={{ width: 16, height: 16 }} />
              }
              {loading ? t('component_replacement_modal_btn_saving') : t('component_replacement_modal_btn_submit')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('lifespan_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default RegisterReplacementModal;
